import Vue from "vue";
import Vuex from "vuex";
import baseVue from "@/main.js";

Vue.use(Vuex);

const getDefaultState = () => {
    return {
        userRole: 0,
        registerEmail: null,
        registerDetails: null,

        patient: null,
        contacts: null,
        profile: null,
        patientMsg: 0,
        providerMsg: 0,
        assessments: null,
        assessmentAnswers: null,
        assessmentsTarget: null,

        provider: null,
        patientList: null,
        clinicianList: null,
        providerNotifs: [],

        practiceList: null,

        appointments: null,
        appointmentNotes: null,

        icdCategories: null,
        icdCodes: null,
        insurers: null,

        modalData: {},
        USER_ROLES: {
            None: 0,
            Patient: 1,
            Provider: 2,
            Clinician: 4,
            Billing: 8,
            Supervisor: 16,
            Staff: 32,
            Owner: 64,
            Admin: 128,
        }
    };
};

const store = new Vuex.Store({
    state: getDefaultState(),
    getters: {
        notificationCount(state) {
            return state.providerNotifs?.length ?? 0;
        },
        profileNotifications(state) {
            if (state.providerNotifs && state.providerNotifs.length > 0)
                return state.providerNotifs.filter((n) => n.type == "profile");
            return [];
        },
        assessNotifications(state) {
            if (state.providerNotifs && state.providerNotifs.length > 0)
                return state.providerNotifs.filter((n) => n.type == "assess");
            return [];
        },
        contactNotifications(state) {
            if (state.providerNotifs && state.providerNotifs.length > 0)
                return state.providerNotifs.filter((n) => n.type == "contact");
            return [];
        },
        baseRole(state) {
            if (state.userRole & state.USER_ROLES.Patient)
                return "Patient";
            if (state.userRole & state.USER_ROLES.Provider)
                return "Provider";
            return "None";
        },
        activeRoles(state) {
            if (state.userRole === null || state.userRole === state.USER_ROLES.None) {
                return [];
            }
            return Object.keys(state.USER_ROLES).filter((role) => {
                return (state.userRole & state.USER_ROLES[role]) > 0;
            });
        },
        provider(state) {
            if (state.provider === null) {
                return [];
            }
            return state.provider;
        },
        patientMsg(state) {
            if (state.patientMsg === null) {
                return 0;
            }
            return state.patientMsg;
        },
        providerMsg(state) {
            if (state.providerMsg === null) {
                return 0;
            }
            return state.providerMsg;
        }
    },
    mutations: {
        setRole(state, role) {
            if (Object.keys(state.USER_ROLES).includes(role)) {
                Vue.set(state, "userRole", state.USER_ROLES[role]);
            } else {
                Vue.set(state, "userRole", role);
            }
        },
        addRole(state, role) {
            if (Object.keys(state.USER_ROLES).includes(role)) {
                Vue.set(state, "userRole", state.userRole | state.USER_ROLES[role]);
            } else if (!isNaN(role) && Object.values(state.USER_ROLES).includes(role)) {
                Vue.set(state, "userRole", state.userRole | role);
            }
        },
        hasRole(state, role) {
            if (state.userRole === null || state.userRole === state.USER_ROLES.None || !Object.keys(state.USER_ROLES).includes(role)) {
                if (role === "None") {
                    return true;
                }
                return false;
            }
            return (state.userRole & state.USER_ROLES[role]) > 0;
        },
        setRegisterEmail(state, email) {
            Vue.set(state, "registerEmail", email);
        },
        setRegisterDetails(state, details) {
            Vue.set(state, "registerDetails", details);
        },
        setAccount(state, account) {
            if (account) {
                Vue.set(state, "userRole", account.Roles);
               
                if (account.Roles & state.USER_ROLES.Provider) {
                    let oldNotifs = baseVue.$localStore.get("providerNotifs");
                    let newNotifs = [
                        ...(oldNotifs ? oldNotifs : []),
                        ...account.Notifications.map((n) => {
                            n.notif_id = Math.floor(Math.random() * 1000000);
                        }),
                    ];

                    Vue.set(state, "providerNotifs", newNotifs);
                    baseVue.$localStore.set("providerNotifs", newNotifs);

                    Vue.delete(account, "Notifications");
                    Vue.set(state, "provider", account);

                } else {
                    Vue.set(state, "patient", account);
                }
            }
        },
        setPatientList(state, patientList) {
            Vue.set(state, "patientList", patientList);
        },
        setClinicianList(state, clinicianList) {
            Vue.set(state, "clinicianList", clinicianList);
        },
        setClinician(state, clinician) {
            Vue.set(state, "clinician", clinician);
        },
        setPatient(state, patient) {
            // Set the patient
            Vue.set(state, "patient", patient);
        },
        setContacts(state, contacts) {
            Vue.set(state, "contacts", contacts);
        },
        clearProfile(state) {
            Vue.set(state, "profile", null);
        },
        setProfilePage(state, { page, data }) {
            // If profile has not been initialized, do so
            if (state.profile == null) Vue.set(state, "profile", {});
            // Set the page's data
            Vue.set(state.profile, page, data);
        },
        setAllAssessmentAnswers(state, answers) {
            Vue.set(state, "assessmentAnswers", answers);
        },
        setAssessmentAnswer(state, answer) {
            // Ensure answers are initialized
            if (state.assessmentAnswers == null)
                Vue.set(state, "assessmentAnswers", {});
            // Set the answer
            Vue.set(state.assessmentAnswers, answer.id, answer.value);
        },
        setProvider(state, provider) {
            if (provider == null) {
                Vue.set(state, "provider", null);
                return;
            }
            if (provider.Notifications) {
                // Set the provider
                let oldNotifs = baseVue.$localStore.get("providerNotifs");
                let newNotifs = [
                    ...(oldNotifs ? oldNotifs : []),
                    ...provider.Notifications.map((n) => {
                        return { ...n, notif_id: Math.floor(Math.random() * 1000000) };
                    }),
                ];

                Vue.set(state, "providerNotifs", newNotifs);
                baseVue.$localStore.set("providerNotifs", newNotifs);

                Vue.delete(provider, "Notifications");
            }
            Vue.set(state, "provider", provider);
        },
        dismissNotification(state, dismiss) {
            if (dismiss === null) {
                // Dismiss all
                Vue.set(state, "providerNotifs", []);
            } else if (isNaN(dismiss)) {
                // Dismiss notification by type
                Vue.set(
                    state,
                    "providerNotifs",
                    state.providerNotifs.filter((n) => {
                        return n.type != dismiss;
                    })
                );
            } else {
                // Dismiss notification by id
                Vue.set(
                    state,
                    "providerNotifs",
                    state.providerNotifs.filter((n) => {
                        return n.notif_id != dismiss;
                    })
                );
            }
            baseVue.$localStore.set("providerNotifs", state.providerNotifs);
        },

        setAssessmentsTarget(state, assessmentsTarget) {
            Vue.set(state, "assessmentsTarget", assessmentsTarget);
        },
        setAssessments(state, { level, assessments }) {
            if (state.assessments == null) Vue.set(state, "assessments", {});
            Vue.set(state.assessments, level, assessments);
        },
        clearAssessments(state) {
            Vue.set(state, "assessments", null);
        },
        setAppointments(state, appointments) {
            Vue.set(state, "appointments", appointments);
        },
        setAppointmentNote(state, { id, note }) {
            if (state.appointmentNotes == null)
                Vue.set(state, "appointmentNotes", {});
            Vue.set(state.appointmentNotes, id, note);
        },
        setModalData(state, { name, data }) {
            Vue.set(state.modalData, name, data);
        },
        setICDCategories(state, categories) {
            Vue.set(state, "icdCategories", categories);
        },
        setICDCodes(state, { category, codes }) {
            if (state.icdCodes == null) Vue.set(state, "icdCodes", {});
            Vue.set(state.icdCodes, category, codes);
        },
        setInsurers(state, insurers) {
            Vue.set(state, "insurers", insurers);
        },
        setInsurances(state, insurances) {
            Vue.set(state, "insurances", insurances);
        },
        clearStoredData(state) {
            Object.assign(state, getDefaultState());
        },
        setPatientMsg(state, msg) {
            Vue.set(state, "patientMsg", msg);
        },
        setProviderMsg(state, msg) {
            Vue.set(state, "providerMsg", msg);
        },
        setPatientTerms(state, terms) {
            Vue.set(state, "patientTerms", terms);
        },
    },
    actions: {
        clearPatient({ commit }) {
            // Clear patient
            commit("setPatient", null);
            commit("clearProfile");
        },
        async logout({ commit, store }) {
            if (store.userRole == null) return;
            // Clear stored data
            commit("clearStoredData");
            // Clear local storage
            localStorage.clear();
        },
        async whoAmI({ commit }) {
            // Get the user's account.
            let account = await baseVue.$api.user.whoAmI();
            if (account) {
                commit("setAccount", account);
            } else {
                commit("clearStoredData");
                alert("Your session has expired.");
            }
        },

        async loadPatient({ commit, dispatch }, targetID) {
            // Clear assessment target if null
            if (targetID == null) {
                commit("setPatient", null);
                commit("clearAssessments");
                commit("setAssessmentsTarget", null);
            }
            // Commit to state
            // Load profile for provided patient ID
            commit(
                "setPatient",
                await baseVue.$api.patient.getProfile({
                    patientID: targetID,
                    page: "Info",
                })
            );
            dispatch("loadAssessments", { level: "latest" });
        },
        async loadProfilePage({ commit, state }, page) {
            // Load profile for provided patient ID
            commit("setProfilePage", {
                page: page,
                data: await baseVue.$api.patient.getProfile({
                    patientID: state.patient?.PatientID,
                    page: page,
                }),
            });
        },
        async loadProvider({ commit }, targetID) {
            // Load provider data.
            commit(
                "setProvider",
                await baseVue.$api.provider.getData({ clinicianID: targetID })
            );
        },
        loadClinician({ commit }, targetID) {
            commit("setClinician", { clinicianID: targetID });
        },
        async loadAssessments({ commit, state }, { patient, level }) {
            if (patient == null) {
                commit("setAssessmentsTarget", {
                    name: state.patient?.Name,
                    id: state.patient?.PatientID,
                });
            } else {
                commit("setAssessmentsTarget", patient);
            }
            // Load assessments for patient
            let assessments = await baseVue.$api.assessment.list({
                patientID: state.assessmentsTarget.id,
                level: level,
            });
            let sorted;
            if (level == "latest")
                sorted = assessments.sort((a, b) => (a.name > b.name ? 1 : -1));
            else
                sorted = assessments.sort((a, b) =>
                    (a.updated ?? a.assigned) > (b.updated ?? b.assigned) ? 1 : -1
                );
            commit("setAssessments", { level: level, assessments: sorted });
        },
        async loadAppointments({ commit }, patientID) {
            commit(
                "setAppointments",
                await baseVue.$api.appointment.list({ patientID: patientID })
            );
        },
        async loadPatientList({ commit }, clinicianID) {
            commit("setPatientList", await baseVue.$api.provider.patients({ clinicianID: clinicianID }));
        },
        async loadClinicianList({ commit }) {
            let _clinicians = await baseVue.$api.provider.clinicians();
            if (_clinicians) commit("setClinicianList", _clinicians);

        },
        async loadPracticeList({ commit }) {
            commit("setPracticeList", await baseVue.$api.provider.practices());
        },
        async loadICDCategories({ commit }) {
            commit("setICDCategories", await baseVue.$api.diagnostic.icdCategories());
        },
        async loadICDCodes({ commit }, category) {
            commit("setICDCodes", {
                category: category,
                codes: await baseVue.$api.diagnostic.icdsFromCategory({
                    categoryID: category,
                }),
            });
        },
        async loadContacts({ commit, state }) {
            commit(
                "setContacts",
                await baseVue.$api.patient.contacts({
                    patientID: state.patient?.PatientID,
                })
            );
        },
        async loadInsurers({ commit }) {
            commit("setInsurers", await baseVue.$api.patient.insurers());
        },
        async loadInsurances({ commit, state }) {
            commit("setInsurances", await baseVue.$api.patient.insurances({
                patientID: state.patient?.PatientID,
            }));
        },
        async loadPatientMsg({ commit }) {
            commit("setPatientMsg", await baseVue.$api.patient.getNewMessages());
        },
        async loadProviderMsg({ commit }) {
            commit("setProviderMsg", await baseVue.$api.provider.getNewMessages());
        },
        async loadPatientTerms({ commit }) {
            commit("setPatientTerms", await baseVue.$api.patient.getTermsConditions());
        },
    },
});

export default store;
